import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, UserPlus, ShieldCheck, User as UserIcon, KeyRound, Store, Pencil, UserCheck, UserX, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { ALL_SECTION_KEYS, SECTION_LABELS } from "@/pages/AppShell";

export default function Users() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ phone: "", password: "", name: "", role: "staff", shop_ids: [] });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toHardDelete, setToHardDelete] = useState(null);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);
  const [sectionTarget, setSectionTarget] = useState(null); // user for the sections dialog
  const [shopsTarget, setShopsTarget] = useState(null); // user for the shop-assignment dialog
  const [passwordTarget, setPasswordTarget] = useState(null); // user for password change
  const [changeOwnPassword, setChangeOwnPassword] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // user for edit dialog

  useEffect(() => {
    if (user && user.role !== "master") {
      nav("/", { replace: true });
    }
  }, [user, nav]);

  const fetch = async () => {
    try {
      const [u, s] = await Promise.all([
        api.get("/users", { params: { include_disabled: showDisabled } }),
        api.get("/shops"),
      ]);
      setItems(u.data.items);
      setShops(s.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDisabled]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, shop_ids: form.shop_ids || [] });
      toast.success("Member added");
      setOpen(false);
      setForm({ phone: "", password: "", name: "", role: "staff", shop_ids: [] });
      fetch();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateShops = async (userId, shopIds) => {
    try {
      await api.patch(`/users/${userId}`, { shop_ids: shopIds });
      toast.success("Shops assigned");
      fetch();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success(`Disabled ${toDelete.name}. History preserved — restore anytime.`);
      setToDelete(null);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const restore = async (target) => {
    try {
      await api.post(`/users/${target.id}/restore`);
      toast.success(`${target.name} re-enabled`);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const hardRemove = async () => {
    if (!toHardDelete) return;
    if (hardDeleteConfirmText !== "DELETE_PERMANENTLY") {
      toast.error("Type DELETE_PERMANENTLY to confirm");
      return;
    }
    try {
      await api.delete(`/users/${toHardDelete.id}/hard`, { params: { confirm: "DELETE_PERMANENTLY" } });
      toast.success(`Permanently removed ${toHardDelete.name}`);
      setToHardDelete(null);
      setHardDeleteConfirmText("");
      fetch();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Team</p>
          <h1 className="font-heading text-3xl tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage who can sign in to your inventory console.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-disabled-toggle-label">
            <Checkbox
              checked={showDisabled}
              onCheckedChange={(v) => setShowDisabled(!!v)}
              data-testid="show-disabled-toggle"
            />
            Show disabled
          </label>
          <Button onClick={() => setOpen(true)} style={{ backgroundColor: "#9E6D5B" }} data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add member
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden" data-testid="users-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Name</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Phone</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Role</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Shop</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Working Hours</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Joined</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className={`data-row border-t border-[hsl(var(--border))] ${u.disabled ? "opacity-60 bg-slate-50/40" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-medium flex items-center gap-2">
                    {u.role === "master" ? (
                      <ShieldCheck className="w-4 h-4 text-[#9E6D5B]" />
                    ) : (
                      <UserIcon className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className={u.disabled ? "line-through" : ""}>{u.name}</span>
                    {u.disabled && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase border border-red-200"
                        title="This user is disabled — cannot log in, but their history is preserved."
                        data-testid={`user-disabled-badge-${u.id}`}
                      >
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="p-3 font-mono-data">{u.phone}</td>
                  <td className="p-3">
                    <span className={`pill ${u.role === "master" ? "pill-high" : "pill-low"}`}>
                      {u.role === "master" ? "MASTER" : u.role}
                    </span>
                  </td>
                  <td className="p-3">
                    {u.role === "master" ? (
                      <span className="text-xs text-muted-foreground">— all shops —</span>
                    ) : (
                      <div className="text-xs">
                        {(u.shop_ids || []).length > 0 
                          ? u.shop_ids.map(sid => shops.find(s => s.id === sid)?.name || sid).join(", ")
                          : "Not assigned"}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {u.role === "master" || u.role === "super_user" ? (
                      <span className="text-xs text-muted-foreground">— 24/7 access —</span>
                    ) : u.working_hours_start && u.working_hours_end ? (
                      <div className="flex items-center gap-1 text-xs font-mono-data">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span>{u.working_hours_start} - {u.working_hours_end}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">No restriction</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {u.created_at ? formatDate(u.created_at) : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditTarget(u)}
                      data-testid={`user-edit-btn-${u.id}`}
                      title="Edit user details"
                    >
                      <Pencil className="w-3.5 h-3.5 text-[#9E6D5B]" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPasswordTarget(u)}
                      data-testid={`user-password-btn-${u.id}`}
                      title="Change password"
                    >
                      <KeyRound className="w-3.5 h-3.5 text-[#9E6D5B]" />
                    </Button>
                    {u.role !== "master" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShopsTarget(u)}
                        data-testid={`user-shops-btn-${u.id}`}
                        title="Assign shops"
                      >
                        <Store className="w-3.5 h-3.5 text-[#9E6D5B]" />
                      </Button>
                    )}
                    {u.role === "staff" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSectionTarget(u)}
                        data-testid={`user-sections-btn-${u.id}`}
                        title="Configure menu access"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-[#9E6D5B]" />
                      </Button>
                    )}
                    {u.id !== user?.id && !u.disabled && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setToDelete(u)}
                        data-testid={`delete-user-btn-${u.id}`}
                        title="Disable user (history preserved)"
                      >
                        <UserX className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    )}
                    {u.id !== user?.id && u.disabled && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => restore(u)}
                          data-testid={`restore-user-btn-${u.id}`}
                          title="Re-enable this user"
                        >
                          <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setToHardDelete(u)}
                          data-testid={`hard-delete-user-btn-${u.id}`}
                          title="Permanently delete (destructive — history will lose actor reference)"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-sm text-muted-foreground">
                    No members yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Add member</DialogTitle>
            <DialogDescription>
              Create a login for a teammate. They can be an owner or staff.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
              <Input
                className="mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-user-name-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
              <Input
                className="mt-1 font-mono-data"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
                data-testid="new-user-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input
                type="password"
                className="mt-1"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                data-testid="new-user-password-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="new-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="super_user">Super User</SelectItem>
                  <SelectItem value="master">MASTER (Full Access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role !== "master" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Shops (Multiple)</Label>
                <div className="mt-1 p-2 border rounded-md max-h-32 overflow-y-auto space-y-1">
                  {shops.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No shops available</p>
                  ) : (
                    shops.map((shop) => (
                      <label key={shop.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={(form.shop_ids || []).includes(shop.id)}
                          onCheckedChange={(checked) => {
                            const current = form.shop_ids || [];
                            const updated = checked
                              ? [...current, shop.id]
                              : current.filter(id => id !== shop.id);
                            setForm({ ...form, shop_ids: updated });
                          }}
                        />
                        <span>{shop.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="new-user-cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#9E6D5B" }}
                data-testid="new-user-submit-btn"
              >
                {saving ? "Saving…" : "Add member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They will lose access immediately and cannot log in. All their past activity,
              sales, and audit history will be preserved and remain visible under their name.
              You can re-enable them anytime from the "Show disabled" list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              data-testid="delete-user-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disable user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toHardDelete} onOpenChange={(o) => { if (!o) { setToHardDelete(null); setHardDeleteConfirmText(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Permanently delete {toHardDelete?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p className="text-destructive font-medium">
                  This is irreversible and destructive.
                </p>
                <p>
                  The user record will be wiped from the database. All audit / activity
                  entries that reference this user will keep their snapshot of the name
                  but the underlying actor link will be permanently broken.
                </p>
                <p className="text-xs text-muted-foreground">
                  We strongly recommend keeping the user disabled instead. Use this only
                  for accidentally-created accounts or for data-cleanup compliance reasons.
                </p>
                <div className="pt-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Type <span className="font-mono-data text-destructive">DELETE_PERMANENTLY</span> to confirm
                  </Label>
                  <Input
                    value={hardDeleteConfirmText}
                    onChange={(e) => setHardDeleteConfirmText(e.target.value)}
                    placeholder="DELETE_PERMANENTLY"
                    className="mt-1 font-mono-data"
                    data-testid="hard-delete-user-confirm-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hard-delete-user-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={hardRemove}
              disabled={hardDeleteConfirmText !== "DELETE_PERMANENTLY"}
              data-testid="hard-delete-user-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SectionAccessDialog
        target={sectionTarget}
        onOpenChange={(v) => { if (!v) setSectionTarget(null); }}
        onSaved={() => fetch()}
      />

      <ShopsAccessDialog
        target={shopsTarget}
        shops={shops}
        onOpenChange={(v) => { if (!v) setShopsTarget(null); }}
        onSaved={() => fetch()}
      />

      <ChangeOwnPasswordDialog
        open={changeOwnPassword}
        onOpenChange={setChangeOwnPassword}
        onDone={() => {}}
      />

      <MasterChangePasswordDialog
        user={passwordTarget}
        onOpenChange={(v) => { if (!v) setPasswordTarget(null); }}
        onDone={() => {}}
      />

      <EditUserDialog
        target={editTarget}
        shops={shops}
        currentUser={user}
        onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        onSaved={() => fetch()}
      />
    </div>
  );
}

function SectionAccessDialog({ target, onOpenChange, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const open = !!target;

  useEffect(() => {
    if (open) {
      const base = Array.isArray(target.allowed_sections) && target.allowed_sections.length > 0
        ? target.allowed_sections
        : ["dashboard"];
      setSelected(new Set(base));
    }
  }, [open, target]);

  if (!open) return null;

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${target.id}`, {
        allowed_sections: Array.from(selected),
      });
      toast.success(`Updated section access for ${target.name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const selectAll = () => setSelected(new Set(ALL_SECTION_KEYS));
  const clearAll = () => setSelected(new Set(["dashboard"]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="user-sections-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Section access — {target.name}
          </DialogTitle>
          <DialogDescription>
            Pick which left-menu sections this staff member can open.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2 -mt-2 mb-1">
          <Button variant="ghost" size="sm" onClick={selectAll} data-testid="user-sections-all">
            Select all
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} data-testid="user-sections-clear">
            Dashboard only
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto p-1">
          {ALL_SECTION_KEYS.map((k) => (
            <label
              key={k}
              htmlFor={`sec-${k}`}
              className="flex items-center gap-2 rounded-md border border-[hsl(var(--border))] p-2 hover:bg-[hsl(var(--muted))] cursor-pointer"
            >
              <Checkbox
                id={`sec-${k}`}
                checked={selected.has(k)}
                onCheckedChange={() => toggle(k)}
                data-testid={`user-section-checkbox-${k}`}
              />
              <span className="text-sm">{SECTION_LABELS[k] || k}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="user-sections-save"
          >
            {saving ? "Saving…" : "Save access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ShopsAccessDialog({ target, shops, onOpenChange, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const open = !!target;

  useEffect(() => {
    if (open) {
      const validIds = new Set(shops.map((s) => s.id));
      const current = Array.isArray(target.shop_ids) ? target.shop_ids : [];
      // Drop stale shop IDs that no longer exist (e.g. shops deleted later) so saving works.
      setSelected(new Set(current.filter((id) => validIds.has(id))));
    }
  }, [open, target, shops]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${target.id}`, { shop_ids: Array.from(selected) });
      toast.success(`Shops updated for ${target.name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const selectAll = () => setSelected(new Set(shops.map((s) => s.id)));
  const clearAll = () => setSelected(new Set());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="user-shops-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Assign shops — {target.name}
          </DialogTitle>
          <DialogDescription>
            Pick which shops {target.name} can access. Leave empty to unassign.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2 -mt-2 mb-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={selectAll}
            disabled={shops.length === 0}
            data-testid="user-shops-all"
          >
            Select all
          </Button>
          <Button variant="ghost" size="sm" onClick={clearAll} data-testid="user-shops-clear">
            Clear
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto p-1">
          {shops.length === 0 ? (
            <p className="text-xs text-muted-foreground p-2">No shops available</p>
          ) : (
            shops.map((shop) => (
              <label
                key={shop.id}
                htmlFor={`shop-${shop.id}`}
                className="flex items-center gap-2 rounded-md border border-[hsl(var(--border))] p-2 hover:bg-[hsl(var(--muted))] cursor-pointer"
              >
                <Checkbox
                  id={`shop-${shop.id}`}
                  checked={selected.has(shop.id)}
                  onCheckedChange={() => toggle(shop.id)}
                  data-testid={`user-shop-checkbox-${shop.id}`}
                />
                <span className="text-sm">{shop.name}</span>
              </label>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="user-shops-save"
          >
            {saving ? "Saving…" : "Save shops"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Password change dialogs
function ChangeOwnPasswordDialog({ open, onOpenChange, onDone }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentPassword("");
      setNewPassword("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      return toast.error("Both fields are required");
    }
    setSaving(true);
    try {
      await api.post("/users/me/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password updated successfully");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-own-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Change My Password</DialogTitle>
          <DialogDescription>
            Update your login password. You'll need your current password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Current Password *
            </Label>
            <Input
              type="password"
              className="mt-1"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              New Password *
            </Label>
            <Input
              type="password"
              className="mt-1"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              data-testid="new-password-input"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="change-password-submit"
            >
              {saving ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MasterChangePasswordDialog({ user: targetUser, onOpenChange, onDone }) {
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const open = !!targetUser;

  useEffect(() => {
    if (open) {
      setNewPassword("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      return toast.error("New password is required");
    }
    setSaving(true);
    try {
      await api.post(`/users/${targetUser.id}/change-password`, {
        new_password: newPassword,
      });
      toast.success(`Password updated for ${targetUser.name}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="master-change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Change Password for {targetUser?.name}
          </DialogTitle>
          <DialogDescription>
            As a MASTER, you can change this user's password without knowing their current password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              New Password *
            </Label>
            <Input
              type="password"
              className="mt-1"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              data-testid="master-new-password-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              User: {targetUser?.name} ({targetUser?.phone})
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="master-change-password-submit"
            >
              {saving ? "Updating…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditUserDialog({ target, shops, currentUser, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ 
    name: "", 
    phone: "", 
    role: "staff", 
    shop_ids: [], 
    allowed_sections: [],
    working_hours_start: "",
    working_hours_end: "",
  });
  const [saving, setSaving] = useState(false);
  const open = !!target;

  useEffect(() => {
    if (open && target) {
      // Filter out any shop_ids that no longer exist in the current shops list
      // (avoids accidentally re-sending orphan ids on save)
      const knownShopIds = new Set((shops || []).map((s) => s.id));
      const cleanShopIds = (target.shop_ids || []).filter((id) =>
        knownShopIds.has(id)
      );
      setForm({
        name: target.name || "",
        phone: target.phone || "",
        role: target.role || "staff",
        shop_ids: cleanShopIds,
        allowed_sections: target.allowed_sections || ["dashboard"],
        working_hours_start: target.working_hours_start || "",
        working_hours_end: target.working_hours_end || "",
      });
    }
  }, [open, target, shops]);

  if (!open) return null;

  const isSelf = target?.id === currentUser?.id;
  const isEditingMaster = target?.role === "master";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.phone.trim()) return toast.error("Phone is required");

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        role: form.role,
      };
      
      // Only send shop_ids for non-master users
      if (form.role !== "master") {
        payload.shop_ids = form.shop_ids;
      }
      
      // Only send allowed_sections for staff
      if (form.role === "staff") {
        payload.allowed_sections = form.allowed_sections.length > 0 ? form.allowed_sections : ["dashboard"];
      }
      
      // Send working hours for non-master/super_user roles
      // Master and super_user always have full access, so working hours don't apply
      if (form.role === "staff") {
        payload.working_hours_start = form.working_hours_start || "";
        payload.working_hours_end = form.working_hours_end || "";
      }

      await api.patch(`/users/${target.id}`, payload);
      toast.success(`User ${form.name} updated`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (key) => {
    setForm((prev) => {
      const current = prev.allowed_sections || [];
      const updated = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      return { ...prev, allowed_sections: updated };
    });
  };

  const toggleShop = (shopId) => {
    setForm((prev) => {
      const current = prev.shop_ids || [];
      const updated = current.includes(shopId)
        ? current.filter((id) => id !== shopId)
        : [...current, shopId];
      return { ...prev, shop_ids: updated };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Edit User — {target?.name}
          </DialogTitle>
          <DialogDescription>
            Update user details, role, and access permissions.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Name */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
            <Input
              className="mt-1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              data-testid="edit-user-name-input"
            />
          </div>

          {/* Phone */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone *</Label>
            <Input
              className="mt-1 font-mono-data"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              required
              data-testid="edit-user-phone-input"
            />
          </div>

          {/* Role - Can't demote self or edit role if only one master */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm({ ...form, role: v })}
              disabled={isSelf && isEditingMaster}
            >
              <SelectTrigger className="mt-1" data-testid="edit-user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="super_user">Super User</SelectItem>
                <SelectItem value="master">MASTER (Full Access)</SelectItem>
              </SelectContent>
            </Select>
            {isSelf && isEditingMaster && (
              <p className="text-xs text-muted-foreground mt-1">You cannot change your own MASTER role.</p>
            )}
          </div>

          {/* Shop Assignment - Only for non-master */}
          {form.role !== "master" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assigned Shops</Label>
              <div className="mt-1 p-2 border rounded-md max-h-32 overflow-y-auto space-y-1">
                {shops.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No shops available</p>
                ) : (
                  shops.map((shop) => (
                    <label key={shop.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={(form.shop_ids || []).includes(shop.id)}
                        onCheckedChange={() => toggleShop(shop.id)}
                        data-testid={`edit-user-shop-${shop.id}`}
                      />
                      <span>{shop.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Section Access - Only for staff */}
          {form.role === "staff" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Menu Access</Label>
              <div className="mt-1 p-2 border rounded-md max-h-40 overflow-y-auto">
                <div className="flex items-center justify-end gap-2 mb-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm({ ...form, allowed_sections: [...ALL_SECTION_KEYS] })}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setForm({ ...form, allowed_sections: ["dashboard"] })}
                  >
                    Dashboard only
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {ALL_SECTION_KEYS.map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer p-1 rounded hover:bg-muted">
                      <Checkbox
                        checked={(form.allowed_sections || []).includes(k)}
                        onCheckedChange={() => toggleSection(k)}
                        data-testid={`edit-user-section-${k}`}
                      />
                      <span className="text-xs">{SECTION_LABELS[k] || k}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Working Hours - Only for staff (master/super_user have 24/7 access) */}
          {form.role === "staff" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Working Hours (24h format)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Staff can only login during these hours. Leave empty for no restriction.
              </p>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Start</Label>
                  <Input
                    type="time"
                    className="mt-1 font-mono-data"
                    value={form.working_hours_start}
                    onChange={(e) => setForm({ ...form, working_hours_start: e.target.value })}
                    placeholder="09:00"
                    data-testid="edit-user-working-start"
                  />
                </div>
                <span className="text-muted-foreground mt-5">to</span>
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">End</Label>
                  <Input
                    type="time"
                    className="mt-1 font-mono-data"
                    value={form.working_hours_end}
                    onChange={(e) => setForm({ ...form, working_hours_end: e.target.value })}
                    placeholder="18:00"
                    data-testid="edit-user-working-end"
                  />
                </div>
                {(form.working_hours_start || form.working_hours_end) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-5"
                    onClick={() => setForm({ ...form, working_hours_start: "", working_hours_end: "" })}
                    title="Clear working hours restriction"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="edit-user-submit-btn"
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
